import { isCaseSettled } from '../utils/helpers.js';
import logger from '../../../../utils/logger';

/**
 * Comprehensive validation service for offer sending
 * Handles all validation logic in one place to prevent duplication
 */

/**
 * Validates cases for sending offers
 * @param {Array} casesToCheck - Array of cases to validate
 * @param {Object} options - Validation options
 * @param {boolean} options.includeSettled - Whether to include settled cases in validation
 * @param {boolean} options.requireOffers - Whether to require offer values
 * @returns {Object} Validation result
 */
export const validateCasesForSending = (casesToCheck, options = {}) => {
  const {
    includeSettled = false,
    requireOffers = true
  } = options;

  if (!casesToCheck || casesToCheck.length === 0) {
    return {
      isValid: false,
      error: 'No cases provided for validation',
      missingOffers: [],
      unsettledCases: [],
      settledCases: []
    };
  }

  // Filter out settled cases completely - they don't need validation
  const unsettledCases = casesToCheck.filter(caseItem => !isCaseSettled(caseItem));
  const settledCases = casesToCheck.filter(caseItem => isCaseSettled(caseItem));
  

  // Check for missing offer values in unsettled cases only
  const missingOffers = unsettledCases.filter(caseItem => {
    if (!caseItem) return false;
    
    const hasOfferValue = caseItem.OfferValue !== null && 
                         caseItem.OfferValue !== 'None' && 
                         caseItem.OfferValue !== '' && 
                         caseItem.OfferValue !== 0 && // Treat 0 as missing offer
                         !isNaN(Number(caseItem.OfferValue)) &&
                         Number(caseItem.OfferValue) > 0; // Must be greater than 0
    
    // Debug logging for first few cases
    if (unsettledCases.indexOf(caseItem) < 3) {
      logger.log(`🔍 [VALIDATION] Case ${caseItem.parcel_id}:`, {
        OfferValue: caseItem.OfferValue,
        hasOfferValue,
        willBeMissing: !hasOfferValue
      });
    }
    
    return !hasOfferValue;
  });

  // Determine if validation passes
  const isValid = !requireOffers || missingOffers.length === 0;

  return {
    isValid,
    missingOffers,
    unsettledCases,
    settledCases,
    totalCases: casesToCheck.length,
    error: isValid ? null : `Missing offers for ${missingOffers.length} case(s)`
  };
};

/**
 * Validates that cases are finalized (ready to send)
 * @param {Array} casesToCheck - Array of cases to validate
 * @returns {Object} Validation result
 */
export const validateCasesAreFinalized = (casesToCheck) => {
  if (!casesToCheck || casesToCheck.length === 0) {
    return {
      isValid: false,
      error: 'No cases provided for validation',
      unfinalizedCases: []
    };
  }

  // Filter out settled cases completely - they don't need finalization validation
  const unsettledCases = casesToCheck.filter(caseItem => !isCaseSettled(caseItem));
  const settledCases = casesToCheck.filter(caseItem => isCaseSettled(caseItem));

  // Check if unsettled cases are finalized (have offers and are ready to send)
  const unfinalizedCases = unsettledCases.filter(caseItem => {
    if (!caseItem) return true; // Missing case is unfinalized
    
    // Check if case has offer value
    const hasOfferValue = caseItem.OfferValue !== null && 
                         caseItem.OfferValue !== 'None' && 
                         caseItem.OfferValue !== '' && 
                         caseItem.OfferValue !== 0 && // Treat 0 as missing offer
                         !isNaN(Number(caseItem.OfferValue)) &&
                         Number(caseItem.OfferValue) > 0; // Must be greater than 0
    
    return !hasOfferValue;
  });

  const isValid = unfinalizedCases.length === 0;

  return {
    isValid,
    unfinalizedCases,
    error: isValid ? null : `Some offers are not finalized. They must be finalized before sending.`
  };
};

/**
 * Comprehensive validation that runs all checks
 * @param {Array} casesToCheck - Array of cases to validate
 * @param {Object} options - Validation options
 * @returns {Object} Complete validation result
 */
export const runAllValidations = (casesToCheck, options = {}) => {
  
  const offerValidation = validateCasesForSending(casesToCheck, options);
  const finalizedValidation = validateCasesAreFinalized(casesToCheck);
  
  const finalResult = {
    isValid: offerValidation.isValid && finalizedValidation.isValid,
    offerValidation,
    finalizedValidation,
    error: offerValidation.error || finalizedValidation.error
  };
  
  logger.log(`🔍 [VALIDATION] Final result:`, {
    isValid: finalResult.isValid,
    error: finalResult.error
  });
  
  return finalResult;
};
